import React, { useState, useEffect } from 'react';
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink } from 'react-router-dom';
import axios from '../services/api';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  Link,
  Icon,
  Image,
  IconButton,
  Alert,
  List,
  Field,
} from '@chakra-ui/react';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError(true);
    }
  }, [token]);

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    };
    return validations;
  };

  const passwordValidations = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordValidations).every(v => v);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'La contraseña es obligatoria';
    } else if (!isPasswordValid) {
      newErrors.password = 'La contraseña no cumple con los requisitos mínimos';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Debes confirmar tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      await axios.post('/auth/reset-password', {
        token,
        newPassword: formData.password,
      });
      setResetSuccess(true);
    } catch (error) {
      console.error('Error al restablecer contraseña:', error);
      const errorMessage = error.response?.data?.message || 'Error al restablecer la contraseña';
      
      if (errorMessage.includes('inválido') || errorMessage.includes('expirado')) {
        setTokenError(true);
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenError) {
    return (
      <Box minH="100vh" bgGradient="linear(to-br, purple.50, white, gold.50)">
        <Box bg="white" boxShadow="sm" py={5}>
          <Container maxW="7xl">
            <HStack justify="center">
              <Image 
                src="/logo y texto cadencya.png" 
                alt="Cadencya" 
                h={{ base: "55px", md: "70px" }}
                w="auto"
                cursor="pointer"
                onClick={() => navigate('/')}
              />
            </HStack>
          </Container>
        </Box>

        <Container maxW="md" py={{ base: 8, md: 16 }}>
          <Box bg="white" p={{ base: 8, md: 10 }} rounded="3xl" boxShadow="xl" w="full">
            <VStack gap={6}>
              <Image src="/capirote-amarillo.png" alt="Error" maxW="120px" />
              <Alert.Root
                status="error"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                rounded="xl"
                p={6}
              >
                <Alert.Indicator boxSize="40px" mr={0} />
                <Heading size="md" mt={4} mb={2}>
                  Enlace inválido o expirado
                </Heading>
                <Alert.Description maxW="sm" fontSize="md">
                  Este enlace no es válido o ya ha expirado. Por favor, solicita un nuevo enlace de recuperación.
                </Alert.Description>
              </Alert.Root>

              <VStack gap={3} pt={4} w="full">
                <Button
                  bg="purple.600"
                  color="white"
                  size="lg"
                  w="full"
                  rounded="full"
                  onClick={() => navigate('/forgot-password')}
                  _hover={{ bg: 'purple.700' }}
                >
                  Solicitar nuevo enlace
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => navigate('/login')}
                >
                  Volver al login
                </Button>
              </VStack>
            </VStack>
          </Box>
        </Container>
      </Box>
    );
  }

  if (resetSuccess) {
    return (
      <Box minH="100vh" bgGradient="linear(to-br, purple.50, white, gold.50)">
        <Box bg="white" boxShadow="sm" py={5}>
          <Container maxW="7xl">
            <HStack justify="center">
              <Image 
                src="/logo y texto cadencya.png" 
                alt="Cadencya" 
                h={{ base: "55px", md: "70px" }}
                w="auto"
                cursor="pointer"
                onClick={() => navigate('/')}
              />
            </HStack>
          </Container>
        </Box>

        <Container maxW="md" py={{ base: 8, md: 16 }}>
          <Box bg="white" p={{ base: 8, md: 10 }} rounded="3xl" boxShadow="xl" w="full">
            <VStack gap={6}>
              <Image src="/capirote-amarillo.png" alt="Éxito" maxW="120px" />
              <Alert.Root
                status="success"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                rounded="xl"
                p={6}
              >
                <Alert.Indicator boxSize="40px" mr={0} />
                <Heading size="md" mt={4} mb={2}>
                  ¡Contraseña actualizada!
                </Heading>
                <Alert.Description maxW="sm" fontSize="md">
                  Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión con tu nueva contraseña.
                </Alert.Description>
              </Alert.Root>

              <Button
                bg="purple.600"
                color="white"
                size="lg"
                w="full"
                rounded="full"
                onClick={() => navigate('/login')}
                _hover={{ bg: 'purple.700' }}
              >
                Ir al login
              </Button>
            </VStack>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bgGradient="linear(to-br, purple.50, white, gold.50)">
      {/* Header simple */}
      <Box bg="white" boxShadow="sm" py={5}>
        <Container maxW="7xl">
          <HStack justify="center">
            <Image 
              src="/logo y texto cadencya.png" 
              alt="Cadencya" 
              h={{ base: "55px", md: "70px" }}
              w="auto"
              cursor="pointer"
              onClick={() => navigate('/')}
            />
          </HStack>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Container maxW="md" py={{ base: 8, md: 16 }}>
        <VStack gap={{ base: 8, md: 12 }}>
          {/* Formulario */}
          <Box
            bg="white"
            p={{ base: 8, md: 10 }}
            rounded="3xl"
            boxShadow="xl"
            w="full"
          >
            <VStack gap={6} mb={8}>
              <Image
                src="/capirote-amarillo.png"
                alt="Nueva contraseña"
                maxW="120px"
              />
              <VStack gap={2}>
                <Heading size="xl" textAlign="center">
                  Nueva contraseña
                </Heading>
                <Text color="gray.600" textAlign="center">
                  Introduce tu nueva contraseña
                </Text>
              </VStack>
            </VStack>

            <form onSubmit={handleSubmit}>
              <VStack gap={5}>
                {/* Error general */}
                {errors.submit && (
                  <Alert.Root status="error" rounded="xl">
                    <Alert.Indicator />
                    {errors.submit}
                  </Alert.Root>
                )}

                {/* Nueva contraseña */}
                <Field.Root invalid={!!errors.password}>
                  <Field.Label fontWeight="600">Nueva contraseña</Field.Label>
                  <InputGroup size="lg">
                    <InputLeftElement>
                      <Icon as={FaLock} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Tu nueva contraseña"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      rounded="xl"
                      bg="gray.50"
                      border="2px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{ borderColor: 'purple.500', bg: 'white' }}
                    />
                    <InputRightElement>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}><Icon as={showPassword ? FaEyeSlash : FaEye} /></IconButton>
                    </InputRightElement>
                  </InputGroup>
                  <Field.ErrorText>{errors.password}</Field.ErrorText>
                </Field.Root>

                {/* Requisitos de contraseña */}
                {formData.password && (
                  <Box w="full" bg="gray.50" p={4} rounded="xl">
                    <Text fontSize="sm" fontWeight="600" mb={2}>
                      La contraseña debe tener:
                    </Text>
                    <List.Root gap={1}>
                      <List.Item fontSize="sm" color={passwordValidations.length ? 'green.600' : 'gray.600'}>
                        <List.Indicator as={passwordValidations.length ? FaCheckCircle : FaTimesCircle} />
                        Al menos 8 caracteres
                      </List.Item>
                      <List.Item fontSize="sm" color={passwordValidations.uppercase ? 'green.600' : 'gray.600'}>
                        <List.Indicator as={passwordValidations.uppercase ? FaCheckCircle : FaTimesCircle} />
                        Una letra mayúscula
                      </List.Item>
                      <List.Item fontSize="sm" color={passwordValidations.lowercase ? 'green.600' : 'gray.600'}>
                        <List.Indicator as={passwordValidations.lowercase ? FaCheckCircle : FaTimesCircle} />
                        Una letra minúscula
                      </List.Item>
                      <List.Item fontSize="sm" color={passwordValidations.number ? 'green.600' : 'gray.600'}>
                        <List.Indicator as={passwordValidations.number ? FaCheckCircle : FaTimesCircle} />
                        Un número
                      </List.Item>
                    </List.Root>
                  </Box>
                )}

                {/* Confirmar contraseña */}
                <Field.Root invalid={!!errors.confirmPassword}>
                  <Field.Label fontWeight="600">Confirmar contraseña</Field.Label>
                  <InputGroup size="lg">
                    <InputLeftElement>
                      <Icon as={FaLock} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirma tu contraseña"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      rounded="xl"
                      bg="gray.50"
                      border="2px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{ borderColor: 'purple.500', bg: 'white' }}
                    />
                    <InputRightElement>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}><Icon as={showConfirmPassword ? FaEyeSlash : FaEye} /></IconButton>
                    </InputRightElement>
                  </InputGroup>
                  <Field.ErrorText>{errors.confirmPassword}</Field.ErrorText>
                </Field.Root>

                {/* Botón de restablecer */}
                <Button
                  type="submit"
                  bg="purple.600"
                  color="white"
                  size="lg"
                  w="full"
                  h="14"
                  rounded="full"
                  loading={isLoading}
                  loadingText="Actualizando..."
                  fontSize="lg"
                  fontWeight="700"
                  _hover={{ 
                    bg: 'purple.700',
                    transform: 'scale(1.02)',
                  }}
                  _active={{ transform: 'scale(0.98)' }}
                >
                  Restablecer contraseña
                </Button>

                {/* Link a login */}
                <Text fontSize="sm" color="gray.600" textAlign="center" pt={2}>
                  ¿Recordaste tu contraseña?{' '}
                  <Link 
                    as={RouterLink}
                    to="/login"
                    color="purple.600" 
                    fontWeight="600"
                    _hover={{ textDecoration: "underline" }}
                  >
                    Iniciar sesión
                  </Link>
                </Text>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default ResetPassword;
